import { CoreLayoutView, CoreTextView, getValueFromPalette, ORIENTATION_ENUM, useThemeHook, type TCoreLayoutOptions, type TCoreLayoutStyleProps, type TCoreTextStyleProps } from '@core/widgets';
import { TCommonData, TViewCommonOption, TWidget, TWidgetCommonOption } from '@libs/types';
import clsx from 'clsx';
import React from 'react';
import { TSeparatorData } from '../../types/data';
import { TSeparatorOption } from '../../types/options';
import { TSeparatorHierarchy, TSeparatorVariant, TextStyle } from '../../types/styles';
import styles from './separator.module.css';

type TSeparatorViewProps = {
    widget?: TWidget<TSeparatorData, TSeparatorOption>;
    uid?: string;
    variant?: TSeparatorVariant;
    hierarchy?: TSeparatorHierarchy;
    text?: string;
    metaText?: string;
    parentStyleNames?: TViewCommonOption['parentStyleNames'];
} & Pick<TWidgetCommonOption, 'margin' | 'isHide' | 'isDisabled'>;

const SUBHEADER_TEXT_STYLE_PROPS: TCoreTextStyleProps = {
    ...TextStyle.subheader,
    main: 'var(--color-grey_72_lc)',
    textTransform: 'uppercase',
};

const FILL_FALLBACK = 'var(--color-grey_8_lc)';
const SHADOW_FALLBACK = 'var(--color-black)';
const SHADOW_HEIGHT_FALLBACK = 16;
const SHADOW_PEAK_ALPHA = '10%';

const SeparatorView = ({
    widget,
    uid: resolvedUid,
    isHide,
    isDisabled,
    variant: resolvedVariant = 'line',
    hierarchy: resolvedHierarchy,
    text: resolvedText,
    metaText: resolvedMetaText,
    margin: resolvedMargin,
    parentStyleNames = [],
}: TSeparatorViewProps) => {
    const isSubheader = resolvedVariant === 'subheader';
    const isShadow = resolvedVariant === 'ShadowUp' || resolvedVariant === 'ShadowDown';
    const containerStyleName = isSubheader ? 'separatorSubheader' : isShadow ? 'separatorShadow' : 'separatorLine';

    const themeWidget = {
        uid: resolvedUid ?? '',
        data: {},
        options: { ...(widget?.options ?? {}), style: containerStyleName, parentStyleNames },
    } as unknown as TWidget<TCommonData, TCoreLayoutOptions>;
    const { palette } = useThemeHook(themeWidget);
    const themedMain = getValueFromPalette(palette, 'main', { isDisabled }) as string | undefined;

    if (isShadow) {
        const shadowColor = themedMain ?? SHADOW_FALLBACK;
        const shadowHeight = (palette.height as number | undefined) ?? SHADOW_HEIGHT_FALLBACK;
        const direction = resolvedVariant === 'ShadowUp' ? 'to top' : 'to bottom';
        const shadowPeak = `color-mix(in srgb, ${shadowColor} ${SHADOW_PEAK_ALPHA}, transparent)`;
        return (
            <CoreLayoutView
                widget={widget}
                uid={resolvedUid}
                isHide={isHide}
                isDisabled={isDisabled}
                className={styles.separatorBlock}
                styleName="separatorShadow"
                parentStyleNames={parentStyleNames}
                styleProps={{ height: shadowHeight, backgroundColor: `linear-gradient(${direction}, ${shadowPeak}, transparent)`, margin: resolvedMargin }}
            />
        );
    }

    const themedFill = themedMain ?? FILL_FALLBACK;

    if (!isSubheader) {
        return (
            <CoreLayoutView
                widget={widget}
                uid={resolvedUid}
                isHide={isHide}
                isDisabled={isDisabled}
                className={styles.separatorBlock}
                styleName="separatorLine"
                parentStyleNames={parentStyleNames}
                styleProps={{ height: 1, minHeight: 1, backgroundColor: themedFill, margin: resolvedMargin }}
            />
        );
    }

    const layoutStyleProps: TCoreLayoutStyleProps = {
        orientation: ORIENTATION_ENUM.HORIZONTAL,
        lineX: 'space_between',
        lineY: 'center',
        padding: { top: 16, right: 24, bottom: 16, left: 24 },
        backgroundColor: resolvedHierarchy === 'secondary' ? themedFill : 'transparent',
        margin: resolvedMargin,
    };

    return (
        <CoreLayoutView
            widget={widget}
            uid={resolvedUid}
            isHide={isHide}
            isDisabled={isDisabled}
            className={clsx(styles.separatorBlock, styles.separatorVariantSubheader)}
            styleName="separatorSubheader"
            parentStyleNames={parentStyleNames}
            styleProps={layoutStyleProps}
        >
            {resolvedText && (
                <CoreTextView
                    uid={resolvedUid ? `title-${resolvedUid}` : undefined}
                    text={resolvedText}
                    isDisabled={isDisabled}
                    parentStyleNames={parentStyleNames}
                    styleName="separatorSubheaderTitle"
                    styleProps={SUBHEADER_TEXT_STYLE_PROPS}
                />
            )}
            {resolvedMetaText && (
                <CoreTextView
                    uid={resolvedUid ? `meta-${resolvedUid}` : undefined}
                    text={resolvedMetaText}
                    isDisabled={isDisabled}
                    parentStyleNames={parentStyleNames}
                    styleName="separatorSubheaderMeta"
                    styleProps={SUBHEADER_TEXT_STYLE_PROPS}
                />
            )}
        </CoreLayoutView>
    );
};

export { SeparatorView };
